import type { AiStreamState, AiStreamStep } from './useAiStream';
import { AI_STREAM_RUNNING } from './useAiStream';

/**
 * THE status line's words — one helper for every surface and level (rev 3.3 §4, §10.3).
 *
 * Every label names the LATEST chunk the backend sent. The browser adds only two
 * things it alone can know: "Sending…" (the request is in flight, no chunk yet) and
 * "Connection quiet…" (no byte — chunk or keep-alive ping — for QUIET_AFTER_MS).
 * Pure: the component translates `labelKey` with `params` (a tool is passed raw; the
 * component turns it into its readable label first).
 */

/** Two missed 15 s pings + margin. */
export const QUIET_AFTER_MS = 35_000;

export type AiStatusTone = 'default' | 'ok' | 'error' | 'quiet';

export interface AiStatusView {
  /** The i18n key of the label. */
  labelKey: string;
  /** The tool step the line is talking about (it is not listed as a chip meanwhile). */
  stepId?: string;
  params: { tool?: string; n?: number; count?: number; phase?: string };
  tone: AiStatusTone;
  /** When the current step started (the step clock); null = no step clock. */
  stepSince: number | null;
}

/** The part of a stream the status line reads. */
export type AiStatusInput = Pick<AiStreamState, 'phase' | 'infra' | 'steps' | 'error' | 'stepStartedAt' | 'lastByteAt' | 'thinkingTokens'> &
  Partial<Pick<AiStreamState, 'failedFrom' | 'log'>>;

/**
 * Backend phase → label keys (as-built vocabulary, 11 phases). `retryKey` when the
 * chunk's `detail` says which attempt this is; `detailKeys` when one phase covers
 * several steps told apart by the `detail` prefix (`pod_starting`: image pull,
 * container start).
 */
export const PHASE_LABELS: Record<string, { labelKey: string; retryKey?: string; detailKeys?: { prefix: string; labelKey: string }[] }> = {
  queued: { labelKey: 'ai.status.queued' },
  claiming: { labelKey: 'ai.status.claiming', retryKey: 'ai.status.claimingRetry' },
  no_warm_pod: { labelKey: 'ai.status.noWarmPod', retryKey: 'ai.status.noWarmPodRetry' },
  pod_pending: { labelKey: 'ai.status.podPending' },
  node_provisioning: { labelKey: 'ai.status.nodeProvisioning' },
  pod_starting: {
    labelKey: 'ai.status.podStarting',
    detailKeys: [
      { prefix: 'Pulling:', labelKey: 'ai.status.podPulling' },
      { prefix: 'Started:', labelKey: 'ai.status.podStarted' },
    ],
  },
  claimed: { labelKey: 'ai.status.claimed' },
  spawning: { labelKey: 'ai.status.spawning', retryKey: 'ai.status.spawningRetry' },
  resuming: { labelKey: 'ai.status.resuming' },
  ready: { labelKey: 'ai.status.ready' },
  saving: { labelKey: 'ai.status.saving' },
};

/** "retry 2: previous pod failed" / "retry 3 of 8, next in 4s" → 2 / 3. */
export function retryOf(detail: string | undefined): number | undefined {
  const m = detail?.match(/\bretry\s+(\d+)/i);
  return m ? Number(m[1]) : undefined;
}

/** A backend phase's label. An unknown (newer) phase shows its own name, never a guess. */
export function phaseLabel(phase: string, detail?: string): Pick<AiStatusView, 'labelKey' | 'params'> {
  const known = PHASE_LABELS[phase];
  if (!known) return { labelKey: 'ai.status.phaseOther', params: { phase } };
  const step = known.detailKeys?.find((d) => detail?.startsWith(d.prefix));
  if (step) return { labelKey: step.labelKey, params: {} };
  const n = retryOf(detail);
  return n !== undefined && known.retryKey ? { labelKey: known.retryKey, params: { n } } : { labelKey: known.labelKey, params: {} };
}

/** How long a "Checked … ✓" of one of several parallel calls holds before the line returns to a call still running. */
export const TOOL_DONE_HOLD_MS = 2_000;

type ToolStep = Extract<AiStreamStep, { kind: 'tool' }>;

/**
 * The tool the line talks about — the LATEST tool event, parallel calls included:
 * the most recent end (`tool_done`) if it came after every start (strictly: a tie is ambiguous) and (while other
 * calls still run) within TOOL_DONE_HOLD_MS; otherwise the most recent call still running.
 */
export function currentTool(steps: AiStreamStep[], now: number): ToolStep | undefined {
  const tools = steps.filter((s): s is ToolStep => s.kind === 'tool');
  if (tools.length === 0) return undefined;
  const running = tools.filter((t) => t.endedAt === undefined);
  const lastRunning = running[running.length - 1];
  const lastEnded = tools.filter((t) => t.endedAt !== undefined).sort((a, b) => (a.endedAt as number) - (b.endedAt as number)).pop();
  const lastStart = Math.max(...tools.map((t) => t.startedAt));
  if (lastEnded && (lastEnded.endedAt as number) > lastStart && (!lastRunning || now - (lastEnded.endedAt as number) < TOOL_DONE_HOLD_MS)) return lastEnded;
  return lastRunning ?? lastEnded;
}

/**
 * The status line for a turn, or null when there is none (nothing running, or the
 * turn has settled). `busy`: the surface still waits for the saved turn.
 */
export function aiStatus(stream: AiStatusInput | null | undefined, busy: boolean, now: number): AiStatusView | null {
  const phase = stream?.phase ?? 'idle';
  const running = AI_STREAM_RUNNING.has(phase);
  if (!running && !busy) return null;
  const since = stream?.stepStartedAt ?? null;

  const last = stream?.lastByteAt ?? null;
  const quiet = last !== null && now - last >= QUIET_AFTER_MS;
  // The live view failed while the turn still runs: the last real label stays until the
  // liveness rule says the connection went quiet (a failure itself is no signal).
  if (!running && phase === 'error') {
    const from = stream?.failedFrom;
    if (quiet || !from || !AI_STREAM_RUNNING.has(from)) return { labelKey: 'ai.status.quiet', params: {}, tone: 'quiet', stepSince: null };
    return aiStatus({ ...stream!, phase: from }, busy, now);
  }
  // Done, the surface is still fetching the saved turn: only the backend's own "saving" is shown.
  if (phase === 'done') {
    return stream?.infra?.phase === 'saving' ? { labelKey: 'ai.status.saving', params: {}, tone: 'default', stepSince: since } : null;
  }
  // Busy with no stream yet: the request is on its way.
  if (!running) return { labelKey: 'ai.status.sending', params: {}, tone: 'default', stepSince: null };

  if (phase === 'pending') return { labelKey: 'ai.status.pending', params: {}, tone: 'default', stepSince: null };
  if (quiet) return { labelKey: 'ai.status.quiet', params: {}, tone: 'quiet', stepSince: null };

  switch (phase) {
    case 'sending':
      return { labelKey: 'ai.status.sending', params: {}, tone: 'default', stepSince: null };
    case 'infra': {
      const infra = stream?.infra;
      if (!infra) return { labelKey: 'ai.status.sending', params: {}, tone: 'default', stepSince: null };
      return { ...phaseLabel(infra.phase, infra.detail), tone: 'default', stepSince: since };
    }
    case 'thinking': {
      const tokens = stream?.thinkingTokens;
      return typeof tokens === 'number'
        ? { labelKey: 'ai.status.thinkingTokens', params: { count: tokens }, tone: 'default', stepSince: since }
        : { labelKey: 'ai.status.thinking', params: {}, tone: 'default', stepSince: since };
    }
    case 'tool': {
      const tool = currentTool(stream?.steps ?? [], now);
      if (!tool) return { labelKey: 'ai.status.thinking', params: {}, tone: 'default', stepSince: since };
      const stepId = tool.id;
      if (tool.endedAt !== undefined) {
        return tool.isError
          ? { labelKey: 'ai.status.toolFailed', params: { tool: tool.name }, tone: 'error', stepSince: tool.endedAt, stepId }
          : { labelKey: 'ai.status.toolOk', params: { tool: tool.name }, tone: 'ok', stepSince: tool.endedAt, stepId };
      }
      return tool.call > 1
        ? { labelKey: 'ai.status.toolRunningCall', params: { tool: tool.name, n: tool.call }, tone: 'default', stepSince: tool.startedAt, stepId }
        : { labelKey: 'ai.status.toolRunning', params: { tool: tool.name }, tone: 'default', stepSince: tool.startedAt, stepId };
    }
    case 'streaming':
    default:
      return { labelKey: 'ai.status.writing', params: {}, tone: 'default', stepSince: since };
  }
}

/** Whole seconds between two instants, never negative. */
export const secondsBetween = (from: number | null | undefined, to: number): number =>
  from === null || from === undefined ? 0 : Math.max(0, Math.floor((to - from) / 1000));

/* ── Pacing: every real step gets its moment on screen ─────────────────────────
 *
 * Several backend phases can land in ONE read (the relay emits the doc's phases
 * together), and parallel tool calls can start in the same millisecond. Showing only
 * the latest would give the others 0 ms. So the status line PLAYS the paced steps —
 * each backend `phase` and each tool start, from the log — in arrival order, each for
 * at least PHASE_MIN_MS; when more than PACE_CATCH_UP steps are waiting it shows the
 * next at once, so the line never trails a live turn by more than ~2 s. Text, thinking
 * and a tool's end are not paced: they show as soon as the paced steps before them
 * have had their time. Consecutive steps that render the SAME label (Pulled / Created /
 * ContainerCreating are all "Pod starting…") share ONE slot, from the first's arrival.
 * Nothing is invented: every label shown is a chunk that arrived,
 * in order — the latest one, or a just-passed one still within its minimum display time.
 * The Technical log is never paced (it lists everything at once).
 */
export const PHASE_MIN_MS = 700;
export const PACE_CATCH_UP = 3;

/** One paced step's on-screen window. */
interface PacedSlot {
  view: AiStatusView;
  start: number;
  end: number;
}

/** Label identity = the rendered string: same key and same parameters. */
const sameLabel = (a: AiStatusView, b: AiStatusView) =>
  a.labelKey === b.labelKey && a.params.tool === b.params.tool && a.params.n === b.params.n && a.params.phase === b.params.phase;

/** The paced steps of a turn, each with the label it had when it arrived; a repeat of the previous label is no new step. */
function pacedViews(stream: AiStatusInput): { arrivedAt: number; view: AiStatusView }[] {
  const steps = new Map((stream.steps ?? []).map((s) => [s.id, s]));
  return (stream.log ?? []).flatMap((e) => {
    if (e.kind === 'phase') return [{ arrivedAt: e.arrivedAt, view: { ...phaseLabel(e.phase, e.detail), tone: 'default' as const, stepSince: e.at } }];
    const t = steps.get(e.stepId);
    if (!t || t.kind !== 'tool') return [];
    const view: AiStatusView =
      t.call > 1
        ? { labelKey: 'ai.status.toolRunningCall', params: { tool: t.name, n: t.call }, tone: 'default', stepSince: t.startedAt, stepId: t.id }
        : { labelKey: 'ai.status.toolRunning', params: { tool: t.name }, tone: 'default', stepSince: t.startedAt, stepId: t.id };
    return [{ arrivedAt: e.arrivedAt, view }];
  }).filter((item, i, all) => i === 0 || !sameLabel(item.view, all[i - 1].view));
}

/** The on-screen schedule: step i starts once it arrived and step i−1 had its minimum (or at once, to catch up). */
export function paceSchedule(items: { arrivedAt: number; view: AiStatusView }[]): PacedSlot[] {
  const slots: PacedSlot[] = [];
  items.forEach((item, i) => {
    const start = i === 0 ? item.arrivedAt : Math.max(item.arrivedAt, slots[i - 1].end);
    const waiting = items.slice(i + 1).filter((x) => x.arrivedAt <= start).length;
    slots.push({ view: item.view, start, end: start + (waiting > PACE_CATCH_UP ? 0 : PHASE_MIN_MS) });
  });
  return slots;
}


/**
 * THE status line (paced): `view` is what to show at `now`, `wakeAt` when it may next
 * change without a new chunk (the caller re-renders then), null when only a chunk can.
 */
export function pacedStatus(
  stream: AiStatusInput | null | undefined,
  busy: boolean,
  clock: number
): { view: AiStatusView | null; wakeAt: number | null } {
  // The 1 s clock can lag the chunk that just arrived: never read the schedule before it.
  const now = Math.max(clock, stream?.lastByteAt ?? clock);
  const latest = aiStatus(stream, busy, now);
  // The browser's own facts are never held back.
  if (!stream || !latest || latest.tone === 'quiet' || latest.labelKey === 'ai.status.pending' || stream.phase === 'error') {
    return { view: latest, wakeAt: null };
  }
  const slots = paceSchedule(pacedViews(stream));
  const i = slots.findIndex((s) => now < s.end && now >= s.start);
  const last = slots[slots.length - 1];
  if (i < 0 || !last || now >= last.end) return { view: latest, wakeAt: null };
  const slot = slots[i];
  // The step on screen is the latest chunk anyway: the live view (its tool end, its clock) wins.
  if (i === slots.length - 1 && sameLabel(slot.view, latest)) return { view: latest, wakeAt: slot.end };
  return { view: slot.view, wakeAt: slot.end };
}
